import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { SERVICE_HEROES } from "@/lib/serviceHeroes";
import "./ServiceHeroes.css";

/**
 * Animated service heroes (Car Care / Home Care / Appliance Repair).
 *  - the supplied picture fills the stage, the words sit on its empty left side
 *  - Book Now is always shown on every slide
 *  - only CSS opacity / transform / background-position animate (no animation library)
 *  - autoplay pauses on hover, keyboard focus, off-screen, hidden tab, and is off for prefers-reduced-motion
 *  - slide timing is the progress bar's own CSS animation (`animationend`), so pausing the bar pauses the slider
 */
export default function ServiceHeroes({ navigate, onSelect, reducedMotion = false, slides = SERVICE_HEROES, seconds = 7.5 }) {
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [inView, setInView] = useState(true);
  const [hidden, setHidden] = useState(false);
  const root = useRef(null);
  const count = slides.length;
  const play = !reducedMotion && count > 1 && !hover && !focus && inView && !hidden;
  const go = useCallback((i) => setIndex(((i % count) + count) % count), [count]);

  useEffect(() => {
    const el = root.current; let io;
    if (el && "IntersectionObserver" in window) { io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 }); io.observe(el); }
    const vis = () => setHidden(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", vis);
    return () => { io && io.disconnect(); document.removeEventListener("visibilitychange", vis); };
  }, []);

  const scrollTo = (to) => {
    const el = document.getElementById(to.slice(1));
    if (el) el.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" }); else window.location.hash = to;
  };
  // Book Now: tell the page which service was chosen (it shows / hides the car plans), then go to that section.
  const book = (s) => {
    if (onSelect) onSelect(s.focus, s.pick);
    const to = s.book;
    if (!to) return;
    if (to.startsWith("#")) setTimeout(() => scrollTo(to), 80);          // wait one tick so a just-shown section has its real height
    else if (typeof navigate === "function") navigate(to);
    else window.location.assign(to);
  };
  // Clicking a service tab (not the automatic rotation) also switches which section is shown below.
  const choose = (i) => { go(i); if (onSelect) onSelect(slides[i].focus, slides[i].pick, true); };

  return (
    <section ref={root} id="service-heroes" className="sh" data-testid="service-heroes" aria-roledescription="carousel" aria-label="NAVI CARE services"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={(e) => { if (e.target.matches && e.target.matches(":focus-visible")) setFocus(true); }}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocus(false); }}>
      <div className="sh-stage">
        {slides.map((s, i) => {
          const active = i === index;
          let w = 0;
          return (
            <article key={s.key} className={`sh-slide${active ? " is-active" : ""}`} role="group" aria-roledescription="slide" aria-label={`${s.tab}, ${i + 1} of ${count}`} inert={!active}>
              <div className="sh-pic">
                <picture>
                  <source media="(max-width: 999px)" srcSet={`${s.image}-1086.webp 1086w, ${s.image}-2172.webp 2172w`} sizes="100vw" />
                  <img src={`${s.image}-2172.webp`} srcSet={`${s.image}-1086.webp 1086w, ${s.image}-2172.webp 2172w`} sizes="100vw" width="2172" height="724" alt={s.alt}
                    loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : "auto"} decoding="async" draggable="false" />
                </picture>
                <span className="sh-shine" aria-hidden="true" />
              </div>
              <div className="sh-copy">
                <p className="sh-kicker sh-a" style={{ "--d": "0s" }}><i aria-hidden="true" />{s.kicker}</p>
                <h2 className="sh-h" aria-label={s.lines.map((l) => l.join(" ")).join(" ")}>
                  {s.lines.map((line, li) => (
                    <span className={`sh-line${li >= s.accentFrom ? " is-accent" : ""}`} key={li} aria-hidden="true">
                      {line.map((word) => { const n = w++; return <span className="sh-wm" key={n}><span className="sh-w" style={{ "--i": n }}>{word}</span></span>; })}
                    </span>
                  ))}
                </h2>
                <p className="sh-quote sh-a" style={{ "--d": ".62s" }}>“{s.quote}”</p>
                <div className="sh-vs sh-a" style={{ "--d": ".8s" }} aria-label={`${s.from.label}: ${s.from.text}. Versus ${s.to.label}: ${s.to.text}.`}>
                  <span className="sh-chip is-old"><small>{s.from.label}</small><b>{s.from.text}</b></span>
                  <span className="sh-vsmark" aria-hidden="true">VS</span>
                  <span className="sh-chip is-new"><small>{s.to.label}</small><b>{s.to.text}</b></span>
                </div>
                <div className="sh-cta sh-a" style={{ "--d": "1s" }}>
                  <button type="button" className="sh-book" data-testid={`service-hero-book-${s.key}`} onClick={() => book(s)} aria-label={`Book Now: ${s.tab}`} tabIndex={active ? 0 : -1}>
                    <span>Book Now</span><ArrowUpRight aria-hidden="true" />
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
      <div className="sh-tabs" role="tablist" aria-label="Choose a service" style={{ "--secs": `${seconds}s` }}>
        {slides.map((s, i) => (
          <button key={s.key} type="button" role="tab" aria-selected={i === index} className={`sh-tab${i === index ? " is-active" : ""}`} onClick={() => choose(i)}>
            <span className="sh-tabname">{s.tab}</span>
            <span className="sh-bar" aria-hidden="true"><i key={`bar-${i}-${i === index ? index : 0}`} style={i === index ? { animationPlayState: play ? "running" : "paused" } : undefined}
              onAnimationEnd={i === index ? () => go(index + 1) : undefined} /></span>
          </button>
        ))}
      </div>
    </section>
  );
}
